import { StyleSheet, Text as RNText, View, type LayoutChangeEvent } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { DealAvailabilityBar } from '@/components/AvailabilityBar';
import { Avatar } from '@/components/Avatar';
import { BACK_CIRCLE_SIZE, BackChevron, goBack } from '@/components/BackChevron';
import { BrandLogo } from '@/components/BrandLogo';
import { headingStyle } from '@/components/Heading';
import { HighlightedText } from '@/components/HighlightedText';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { markFirstAmount } from '@/lib/highlightMarkup';
import type { Deal } from '@/types/deal';

export type DealDetailHeroProps = {
  deal: Deal;
  /** Reports the hero's height, so the screen knows when it has scrolled away. */
  onLayout?: (e: LayoutChangeEvent) => void;
  testID?: string;
};

/**
 * Full-bleed Deep Green header for the deal page: back chevron, the offer's
 * brand pill, the headline, and the server's one-line pitch
 * (`marketing_copy.hero_subhead`). The page's content sheet overlaps its
 * bottom edge (see `DEAL_HERO_OVERLAP`), so the extra bottom padding here is
 * what the sheet covers, not dead space.
 *
 * Full-bleed: it runs up under the status bar and takes the top inset
 * itself (the screen has no safe-area strip, like a native large header).
 */
export function DealDetailHero({ deal, onLayout, testID }: DealDetailHeroProps) {
  const initials = deal.brand_name.slice(0, 2).toUpperCase();
  const subhead = deal.marketing_copy?.hero_subhead;

  return (
    <SafeAreaView edges={['top']} style={styles.hero} onLayout={onLayout} testID={testID}>
      <RNText
        style={styles.watermark}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        M
      </RNText>
      <View style={styles.headerRow}>
        {/* Opened from a link or a push there is no history: land on Deals, not Home. */}
        <BackChevron tone="onDark" variant="circle" onPress={() => goBack('/deals')} />
        <View style={styles.brandPill}>
          <BrandLogo
            url={deal.brand_logo_url}
            size={32}
            radius={radii.base}
            inset={4}
            fallback={<Avatar initials={initials} size={32} tone="electric" />}
          />
          <Text variant="bodyBold" color="frost" numberOfLines={1} style={styles.brandName}>
            {deal.display_name}
          </Text>
        </View>
        {/* Balances the back button so the brand pill sits centered. */}
        <View style={styles.headerSpacer} />
      </View>

      <HighlightedText
        style={headingStyle('4xl', 'inverse')}
        highlightStyle={styles.titleAmount}
        accessibilityRole="header"
      >
        {markFirstAmount(deal.title)}
      </HighlightedText>
      {subhead ? <Text color="textOnDarkStrong">{subhead}</Text> : null}
      {/* Spots left, for a capped deal only: an uncapped one would be a full
          bar saying "No monthly limit". */}
      {deal.monthly_referral_cap != null ? <DealAvailabilityBar deal={deal} tone="dark" /> : null}
    </SafeAreaView>
  );
}

/** How far the content sheet below the hero rides up over its bottom edge. */
export const DEAL_HERO_OVERLAP = radii['2xl'];

const styles = StyleSheet.create({
  // overflow:hidden clips the watermark to the hero's box.
  hero: {
    backgroundColor: colors.deepGreen,
    paddingHorizontal: spacing.base,
    paddingTop: spacing.sm,
    paddingBottom: spacing.lg + DEAL_HERO_OVERLAP,
    gap: spacing.base,
    overflow: 'hidden',
  },
  // Scaled MP watermark per brand kit page 16 (same treatment as
  // DealHeroCard and the home hero): Frost @ 8%, rotated, bleeding off the
  // top-right edge. pointerEvents=none so it never takes a tap; hidden from
  // screen readers, which would otherwise announce "MP" first.
  watermark: {
    position: 'absolute',
    top: -8,
    right: -36,
    fontFamily: fonts.numeric,
    fontSize: fontSizes.watermark,
    lineHeight: fontSizes.watermark,
    color: colors.watermarkOnDark,
    transform: [{ rotate: '-8deg' }],
    letterSpacing: -10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerSpacer: {
    width: BACK_CIRCLE_SIZE,
  },
  // Shrinks (and the name truncates) rather than pushing into the spacer.
  brandPill: {
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingLeft: spacing.xs,
    paddingRight: spacing.md,
    borderRadius: radii.full,
    backgroundColor: colors.glassFrostFill,
    borderWidth: 1,
    borderColor: colors.glassFrostBorder,
  },
  brandName: {
    flexShrink: 1,
  },
  titleAmount: {
    color: colors.electricGreen,
  },
});
